"use client";

import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  SimpleGrid,
  Progress,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { useAuthStore } from "@/_stores/useAuthStore";
import type { AnalysisResult } from "../mockdata/analysisData";
import { getScoreColor, getScoreLabelKey } from "../utils/helpers";
import { useBenchmarkContext } from "../_context/BenchmarkContext";
import { BenchmarkAuthenticatedATSPreview } from "./BenchmarkAuthenticatedATSPreview";
import { Emoji } from "@/_components/ui/Emoji";
import { KnootLogo2026 } from "@/_images/svgs/chakra-icons/Logo";

interface ATSTabProps {
  data: AnalysisResult;
  onSignupRequest: () => void;
}

const MockPDFPreview = () => (
  <Box
    bg="white"
    rounded="lg"
    p={8}
    minH="500px"
    color="gray.800"
    fontSize="xs"
    lineHeight="1.7"
    fontFamily="'Courier New', monospace"
  >
    <Box mb={4} pb={3} borderBottom="2px solid" borderColor="gray.200">
      <Text fontSize="lg" fontWeight="black" color="gray.900">
        NGUYEN VAN A
      </Text>
      <Text fontSize="xs" color="gray.500">
        nguyenvana@email.com · +84 912 345 678 · Ha Noi, Vietnam
      </Text>
    </Box>
    <Box mb={4}>
      <Text fontWeight="bold" fontSize="sm" color="indigo.600" mb={1}>
        SUMMARY
      </Text>
      <Text color="gray.700">
        Senior Frontend Developer with 5+ years of experience building scalable
        web applications using React, TypeScript, and Next.js. Passionate about
        performance optimisation and clean code architecture.
      </Text>
    </Box>
    <Box mb={4}>
      <Text fontWeight="bold" fontSize="sm" color="indigo.600" mb={1}>
        EXPERIENCE
      </Text>
      <Box mb={3}>
        <HStack justify="space-between">
          <Text fontWeight="semibold">
            Senior Frontend Developer — TechCorp
          </Text>
          <Text color="gray.400">2021 – Present</Text>
        </HStack>
        <Text color="gray.600">
          • Reduced bundle size by 40% through code splitting and lazy loading
        </Text>
        <Text color="gray.600">
          • Led migration from CRA to Next.js 13, improving Lighthouse score
          from 65→94
        </Text>
        <Text color="gray.600">
          • Mentored 3 junior developers and conducted weekly code reviews
        </Text>
      </Box>
    </Box>
    <Box mb={4}>
      <Text fontWeight="bold" fontSize="sm" color="indigo.600" mb={1}>
        SKILLS
      </Text>
      <Text color="gray.700">
        React · TypeScript · Next.js · Node.js · GraphQL · TailwindCSS · AWS ·
        Git · Figma
      </Text>
    </Box>
    <Box>
      <Text fontWeight="bold" fontSize="sm" color="indigo.600" mb={1}>
        EDUCATION
      </Text>
      <Text color="gray.700">
        B.Sc. Computer Science — Hanoi University of Science and Technology
        (2015–2019)
      </Text>
    </Box>
  </Box>
);

function GuestATSPreview({ onSignupRequest }: { onSignupRequest: () => void }) {
  const t = useTranslations("resume_benchmark");

  return (
    <Box position="relative" rounded="2xl" overflow="hidden">
      <Box filter="blur(6px)" pointerEvents="none" userSelect="none">
        <MockPDFPreview />
      </Box>

      <Box
        position="absolute"
        inset={0}
        rounded="2xl"
        bg="linear-gradient(to top, rgba(9,9,18,0.98) 35%, rgba(9,9,18,0.5) 100%)"
        display="flex"
        alignItems="flex-end"
        justifyContent="center"
        pb={10}
      >
        <VStack gap={3} textAlign="center" px={6}>
          <Box
            boxSize={14}
            rounded="full"
            bg="blackAlpha.300"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="2xl"
          >
            <KnootLogo2026 boxSize={6} />
          </Box>
          <Text fontSize="md" fontWeight="bold" color="neutral.50">
            {t("ats.guest.title")}
          </Text>
          <Text fontSize="xs" color="neutral.300" maxW="340px">
            {t("ats.guest.description")}
          </Text>
          <HStack gap={3} flexWrap="wrap" justify="center">
            <Button
              px={6}
              py={2}
              rounded="full"
              bg="primary.600"
              color="white"
              fontWeight="bold"
              fontSize="sm"
              fontFamily="mono"
              _hover={{
                bg: "blackAlpha.400",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 24px rgba(139,92,246,0.4)",
              }}
              transition="all 0.2s"
              onClick={onSignupRequest}
            >
              {t("ats.guest.cta")}
            </Button>
          </HStack>
          <Text fontSize="xs" color="neutral.500">
            {t("ats.guest.footnote")}
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}

export const ATSTab = ({ data, onSignupRequest }: ATSTabProps) => {
  const t = useTranslations("resume_benchmark");
  const token = useAuthStore((s) => s.token);
  const { resumeId } = useBenchmarkContext();
  const color = getScoreColor(data.atsScore);
  const fileBasename = data.candidateName || t("ats.defaultFileBasename");

  return (
    <VStack gap={8} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
        <Box
          p={6}
          rounded="3xl"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <Text fontSize="xs" color="neutral.300" mb={3}>
            {t("ats.scoreLabel")}
          </Text>
          <HStack gap={4} align="flex-end" mb={4}>
            <Text
              fontSize="5xl"
              fontWeight="medium"
              color={`${color}.400`}
              lineHeight="1"
              fontFamily="mono"
            >
              {data.atsScore}
              <Text
                as="span"
                fontSize="sm"
                fontWeight="light"
                color="neutral.500"
                fontFamily="mono"
              >
                {t("ats.scoreOutOf")}
              </Text>
            </Text>
            <VStack align="flex-start" gap={0} mb={1}>
              {/* <Text fontSize="xs" color="neutral.500">
                out of 100
              </Text> */}
              <Badge
                px={2}
                py={0.5}
                rounded="full"
                fontSize="2xs"
                bg={`${color}.900`}
                color={`${color}.300`}
              >
                {t(`scoreLabel.${getScoreLabelKey(data.atsScore)}`)}
              </Badge>
            </VStack>
          </HStack>
          <Progress.Root value={data.atsScore} colorPalette={color} size="sm">
            <Progress.Track bg="whiteAlpha.100" rounded="full">
              <Progress.Range rounded="full" />
            </Progress.Track>
          </Progress.Root>
          <Box id="ats-info" mt={5} pt={5}>
            <Text fontSize="xs" color="neutral.300" lineHeight="1.7" mb={4}>
              <Text as="span" fontWeight="semibold" color="neutral.100">
                {t("ats.scoreLabel")}
              </Text>{" "}
              {t("ats.scoreDescription")}
            </Text>
          </Box>
        </Box>

        <Box
          p={6}
          rounded="3xl"
          bg="rgba(239,68,68,0.06)"
          border="1px solid"
          borderColor="red.500"
        >
          <Text fontSize="xs" color="error.400" mb={3}>
            {t("ats.issuesDetected")}
          </Text>
          <VStack align="stretch" gap={2}>
            {data.atsIssues.map((issue, i) => (
              <HStack key={i} gap={2} align="flex-start">
                <Emoji emoji="fire" size={5} />
                <Text fontSize="sm" color="error.100" lineHeight="1.6">
                  {issue}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Box>
      </SimpleGrid>

      <Box>
        <HStack gap={2} mb={4}>
          <Emoji emoji="file-folder" size={6} />
          <Text fontSize="lg" fontWeight="semibold" color="neutral.50">
            {t("ats.optimisedResume")}
          </Text>
          <Badge
            px={2}
            py={0.5}
            rounded="full"
            fontSize="2xs"
            bg="primary.700"
            color="primary.100"
          >
            {t("ats.aiRewrittenBadge")}
          </Badge>
        </HStack>

        {!token ? (
          <GuestATSPreview onSignupRequest={onSignupRequest} />
        ) : resumeId ? (
          <BenchmarkAuthenticatedATSPreview
            resumeId={resumeId}
            fileBasename={fileBasename}
          />
        ) : null}
      </Box>
    </VStack>
  );
};
